import { useState } from 'react'
import { Link } from 'react-router-dom'
import DocsSidebar from '../components/DocsSidebar'
import Footer from '../components/Footer'
import SEO from '../components/SEO'
import SiteNav from '../components/SiteNav'
import {
  AGENT_DEPLOY_COMMAND,
  AGENT_INSTALL_EXAMPLES,
  OTHER_AGENTS_INSTALL_COMMAND,
} from '../lib/agentCommands'
import '../styles/docs.css'

type IconName = 'book' | 'check' | 'copy' | 'external' | 'file' | 'plugin' | 'terminal'

interface Platform {
  name: string
  id: string
  sourceLabel: string
  sourceHref?: string
  plugin: string
  skills: string
  install: string[]
  update: string[]
  usage: string
}

function installCommandsFor(name: string) {
  return AGENT_INSTALL_EXAMPLES.find((example) => example.name === name)?.installCommands ?? []
}

function updateCommandsFor(name: string) {
  return AGENT_INSTALL_EXAMPLES.find((example) => example.name === name)?.updateCommands ?? []
}

const PLATFORMS: Platform[] = [
  {
    name: 'Claude Code',
    id: 'claude-code',
    sourceLabel: 'opendeploy-claude-plugin',
    sourceHref: 'https://github.com/opendeploy-dev/opendeploy-claude-plugin',
    plugin: 'Claude plugin: opendeploy',
    skills: 'opendeploy',
    install: installCommandsFor('Claude Code'),
    update: updateCommandsFor('Claude Code'),
    usage: `After install, ask Claude Code: "${AGENT_DEPLOY_COMMAND}".`,
  },
  {
    name: 'Codex',
    id: 'codex',
    sourceLabel: 'opendeploy-codex-plugin',
    sourceHref: 'https://github.com/opendeploy-dev/opendeploy-codex-plugin',
    plugin: 'Codex plugin: opendeploy',
    skills: 'opendeploy',
    install: installCommandsFor('Codex'),
    update: updateCommandsFor('Codex'),
    usage: `After install, ask Codex: "${AGENT_DEPLOY_COMMAND}".`,
  },
  {
    name: 'Cursor',
    id: 'cursor',
    sourceLabel: 'opendeploy-cursor-plugin',
    sourceHref: 'https://github.com/opendeploy-dev/opendeploy-cursor-plugin',
    plugin: 'Cursor plugin: opendeploy',
    skills: 'opendeploy',
    install: installCommandsFor('Cursor'),
    update: updateCommandsFor('Cursor'),
    usage: `After install, ask Cursor: "${AGENT_DEPLOY_COMMAND}".`,
  },
  {
    name: 'OpenCode',
    id: 'opencode',
    sourceLabel: 'opendeploy-opencode',
    sourceHref: 'https://github.com/opendeploy-dev/opendeploy-opencode',
    plugin: 'OpenCode skills + commands',
    skills: 'opendeploy, /opendeploy, /od, /deploy',
    install: installCommandsFor('OpenCode'),
    update: updateCommandsFor('OpenCode'),
    usage: `After install, ask OpenCode: "${AGENT_DEPLOY_COMMAND}".`,
  },
  {
    name: 'OpenClaw',
    id: 'openclaw',
    sourceLabel: 'opendeploy-openclaw-plugin',
    sourceHref: 'https://github.com/opendeploy-dev/opendeploy-openclaw-plugin',
    plugin: 'OpenClaw plugin: opendeploydev',
    skills: 'opendeploy',
    install: installCommandsFor('OpenClaw'),
    update: updateCommandsFor('OpenClaw'),
    usage: `After install, ask OpenClaw: "${AGENT_DEPLOY_COMMAND}".`,
  },
]

const HANDOFF_RULES = [
  'Read opendeploy docs before creating a deployment.',
  'Inspect framework, dependencies, environment variables, ports, build command, and start command.',
  'Create the deployment through opendeploy instead of deploying directly to infrastructure.',
  'Fix safe blocking build errors and return the live URL when the app is ready.',
]

function DocsIcon({ name }: { name: IconName }) {
  const common = {
    fill: 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    strokeWidth: 1.8,
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {name === 'book' && (
        <path {...common} d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v16H7a3 3 0 0 0-3 3V5.5Zm0 0V22M8 7h8M8 11h6" />
      )}
      {name === 'check' && <path {...common} d="m5 12 4 4L19 6" />}
      {name === 'copy' && <path {...common} d="M8 8h11v13H8V8Zm-3 8H4V3h11v1" />}
      {name === 'external' && <path {...common} d="M10 6H6v12h12v-4M13 5h6v6m0-6-8 8" />}
      {name === 'file' && <path {...common} d="M7 3h7l4 4v14H7V3Zm7 0v5h5M10 12h6M10 16h6" />}
      {name === 'plugin' && (
        <>
          <circle {...common} cx="8" cy="8" r="2.6" />
          <circle {...common} cx="16" cy="8" r="2.6" />
          <circle {...common} cx="8" cy="16" r="2.6" />
          <circle {...common} cx="16" cy="16" r="2.6" />
        </>
      )}
      {name === 'terminal' && <path {...common} d="m5 7 5 5-5 5m7 0h7" />}
    </svg>
  )
}

function fallbackCopy(text: string) {
  const textArea = document.createElement('textarea')
  textArea.value = text
  textArea.style.position = 'fixed'
  textArea.style.opacity = '0'
  document.body.appendChild(textArea)
  textArea.select()

  try {
    document.execCommand('copy')
  } finally {
    document.body.removeChild(textArea)
  }
}

function InstallCommandList({ commands }: { commands: string[] }) {
  return (
    <div className="od-docs-install-commands" role="list">
      {commands.map((command, index) => (
        <div className="od-docs-install-command-row" role="listitem" key={`${index}-${command}`}>
          <span className="od-docs-install-command-index" aria-hidden="true">
            {index + 1}
          </span>
          <code>{command}</code>
        </div>
      ))}
    </div>
  )
}

export default function AgentPlatformsDocs() {
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const copyText = (id: string, value: string) => {
    const markCopied = () => {
      setCopiedId(id)
      window.setTimeout(() => setCopiedId(null), 1400)
    }

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(value).then(markCopied).catch(() => {
        fallbackCopy(value)
        markCopied()
      })
      return
    }

    fallbackCopy(value)
    markCopied()
  }

  const copyInstall = (platform: Platform) => {
    copyText(platform.id, platform.install.join('\n'))
  }

  return (
    <>
      <SEO
        title="Agent Platforms, Plugins & Skills"
        description="Install and update OpenDeploy for Claude Code, Codex, Cursor, OpenCode, and OpenClaw."
        path="/docs/agent-platforms-plugins-skills"
      />
      <div className="od-docs-page">
        <SiteNav />
        <div className="od-docs-frame od-docs-frame--article">
          <DocsSidebar />

          <main className="od-docs-article">
            <nav className="od-docs-breadcrumb" aria-label="Breadcrumb">
              <Link to="/docs">Docs</Link>
              <span>/</span>
              <span>Agent platforms</span>
            </nav>

            <header className="od-docs-article-hero">
              <p className="od-docs-kicker">Agent deployment</p>
              <h1>Agent Platforms, Plugins & Skills</h1>
              <p>
                Install the opendeploy surface your coding agent can use. Each plugin teaches the
                agent to inspect the current project, read the opendeploy flow, create a deployment,
                fix safe blocking build errors, and return a live URL.
              </p>
              <div className="od-docs-meta">
                <span>Updated May 7, 2026</span>
                <span>{PLATFORMS.length} platforms</span>
                <span>Plugin + setup ready</span>
              </div>
            </header>

            <section id="overview" className="od-docs-article-section">
              <div className="od-docs-section-copy">
                <p className="od-docs-kicker">Overview</p>
                <h2>What the agent does</h2>
                <p>Install the native plugin or skill, then give the agent one deploy instruction.</p>
              </div>

              <ul className="od-docs-check-list" aria-label="Agent handoff requirements">
                {HANDOFF_RULES.map((rule) => (
                  <li key={rule}>
                    <DocsIcon name="check" />
                    <span>{rule}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section id="plugins-and-skills" className="od-docs-article-section">
              <div className="od-docs-section-copy">
                <p className="od-docs-kicker">Plugins and skills</p>
                <h2>Plugins and skills</h2>
                <p>Use the native plugin or skill for your editor. The plugin installs the agent surface; the setup skill handles CLI install or update later with user consent.</p>
              </div>

              <div className="od-docs-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Platform</th>
                      <th>Plugin or package</th>
                      <th>Skills or commands</th>
                      <th>Git or source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {PLATFORMS.map((platform) => (
                      <tr key={platform.name}>
                        <td>{platform.name}</td>
                        <td>{platform.plugin}</td>
                        <td>{platform.skills}</td>
                        <td>
                          {platform.sourceHref ? (
                            <a href={platform.sourceHref} target="_blank" rel="noopener noreferrer">
                              {platform.sourceLabel}
                              <DocsIcon name="external" />
                            </a>
                          ) : (
                            platform.sourceLabel
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section id="install" className="od-docs-article-section">
              <div className="od-docs-section-copy">
                <p className="od-docs-kicker">Install</p>
                <h2>Install by platform</h2>
                <p>Copy the install commands for the agent environment where you want opendeploy available. Use the update commands later when the plugin reports that new skill text is available.</p>
              </div>

              <div className="od-docs-install-list">
                {PLATFORMS.map((platform) => (
                  <article key={platform.name} id={platform.id} className="od-docs-install-card">
                    <div className="od-docs-install-head">
                      <div>
                        <h3>{platform.name}</h3>
                        <p>{platform.plugin}</p>
                      </div>
                      <button type="button" onClick={() => copyInstall(platform)}>
                        <DocsIcon name="copy" />
                        <span>{copiedId === platform.id ? 'Copied' : 'Copy'}</span>
                      </button>
                    </div>
                    <InstallCommandList commands={platform.install} />
                    <div className="od-docs-install-subsection">
                      <h4>Update</h4>
                      <InstallCommandList commands={platform.update} />
                    </div>
                    <p>{platform.usage}</p>
                  </article>
                ))}

                <article id="other-agents-command" className="od-docs-install-card">
                  <div className="od-docs-install-head">
                    <div>
                      <h3>Other agents</h3>
                      <p>Paste this into agents without a native opendeploy plugin</p>
                    </div>
                    <button type="button" onClick={() => copyText('other-agents-command', OTHER_AGENTS_INSTALL_COMMAND)}>
                      <DocsIcon name="copy" />
                      <span>{copiedId === 'other-agents-command' ? 'Copied' : 'Copy'}</span>
                    </button>
                  </div>
                  <InstallCommandList commands={[OTHER_AGENTS_INSTALL_COMMAND]} />
                </article>
              </div>
            </section>
          </main>

          <aside className="od-docs-toc" aria-label="On this page">
            <p>On this page</p>
            <a href="#overview">Overview</a>
            <a href="#plugins-and-skills">Plugins and skills</a>
            <a href="#install">Install</a>
          </aside>
        </div>
        <Footer />
      </div>
    </>
  )
}
